/**
 * EnvIndexer: handles .env file indexing (keys + type metadata only, never values).
 * Extracted from IndexingPipeline to reduce pipeline complexity.
 */
import fs from 'node:fs';
import path from 'node:path';
import fg from 'fast-glob';
import picomatch from 'picomatch';
import type { TraceMcpConfig } from '../config.js';
import type { Store } from '../db/store.js';
import { logger } from '../logger.js';
import { descendantExcludeGlobs } from '../registry.js';
import { initContentHasher } from '../util/hash.js';
import { parseEnvFile } from '../utils/env-parser.js';
import { GitignoreMatcher } from '../utils/gitignore.js';
import { hashContent } from '../utils/hasher.js';
import { validatePath } from '../utils/security.js';
import { TraceignoreMatcher } from '../utils/traceignore.js';

const ENV_GLOB = ['.env', '.env.*', '.env.local', '**/.env', '**/.env.*'];
const ENV_FILE_BASENAME = /^\.env(\.[^/]*)?$/;

function isEnvFilePattern(pattern: string): boolean {
  const basename = pattern.split('/').pop() ?? pattern;
  return ENV_FILE_BASENAME.test(basename);
}

export class EnvIndexer {
  private traceignore: TraceignoreMatcher;
  private gitignore: GitignoreMatcher | undefined;

  constructor(
    private store: Store,
    private config: TraceMcpConfig,
    private rootPath: string,
    traceignore?: TraceignoreMatcher,
    gitignore?: GitignoreMatcher,
  ) {
    this.traceignore = traceignore ?? new TraceignoreMatcher(rootPath, config.ignore);
    this.gitignore =
      gitignore ??
      (config.ignore?.gitignore === false ? undefined : new GitignoreMatcher(rootPath));
  }

  async indexEnvFiles(force: boolean): Promise<void> {
    try {
      await this.indexEnvFilesUnsafe(force);
    } catch (err) {
      logger.warn({ err }, 'Env indexing failed — skipping');
    }
  }

  private async indexEnvFilesUnsafe(force: boolean): Promise<void> {
    await initContentHasher();
    // Default config.exclude contains `**/.env` / `**/.env.*` to keep env files out of
    // the code index. EnvIndexer only records keys + inferred types/formats (no values),
    // so those patterns would wrongly hide our input — filter them before globbing.
    // descendantExcludeGlobs: a registered descendant owns its own subtree, so an
    // umbrella root must not index the descendant's .env files into a second DB
    // (#209 / TRA-468 — same gate collectFiles() and filterIndexablePaths apply).
    const descendantGlobs = descendantExcludeGlobs(this.rootPath);
    const ignore = [...this.config.exclude.filter((p) => !isEnvFilePattern(p)), ...descendantGlobs];

    // followSymbolicLinks defaults to false (config.follow_symlinks) — a directory
    // symlink cycling back to an ancestor (e.g. Ansible Molecule's
    // `roles/<role> -> ../../../` layout) would otherwise make fast-glob recurse
    // until ENAMETOOLONG (#218). suppressErrors: true is a second layer of defense
    // on top of the outer try/catch — traversal errors should skip .env discovery,
    // not abort indexing.
    const envPaths = await fg(ENV_GLOB, {
      cwd: this.rootPath,
      ignore,
      dot: true,
      absolute: false,
      onlyFiles: true,
      followSymbolicLinks: this.config.follow_symlinks,
      suppressErrors: true,
    });

    if (envPaths.length === 0) return;

    logger.info({ count: envPaths.length }, 'Indexing .env files (keys only)');

    // Post-glob gates mirroring filterIndexablePaths: the fast-glob ignore
    // above handles the common case, but these catch anything the glob
    // misses so event-driven callers can't re-add excluded rows either.
    const ownedByDescendant = descendantGlobs.length
      ? picomatch(descendantGlobs, { dot: true })
      : undefined;

    for (const relPath of envPaths) {
      if (this.traceignore.isIgnored(relPath)) {
        logger.debug({ file: relPath }, '.env file skipped by .traceignore');
        continue;
      }

      const relPosix = relPath.split(path.sep).join('/');
      if (ownedByDescendant?.(relPosix)) {
        logger.debug(
          { file: relPath },
          '.env file skipped: owned by a more-specific registered project',
        );
        continue;
      }
      if (this.gitignore?.isIgnored(relPosix)) {
        logger.debug({ file: relPath }, '.env file skipped: git-ignored');
        continue;
      }

      const absPath = path.resolve(this.rootPath, relPath);

      const pathCheck = validatePath(relPath, this.rootPath);
      if (pathCheck.isErr()) continue;

      let fileMtimeMs: number | null = null;
      try {
        const stat = fs.lstatSync(absPath);
        fileMtimeMs = Math.floor(stat.mtimeMs);
      } catch {
        // stat failed — readFileSync below will handle ENOENT
      }

      let content: string;
      try {
        content = fs.readFileSync(absPath, 'utf-8');
      } catch {
        logger.warn({ file: relPath }, 'Cannot read .env file');
        continue;
      }

      const hash = hashContent(Buffer.from(content));
      const existing = this.store.getFile(relPath);

      if (!force && existing && existing.content_hash === hash) {
        if (fileMtimeMs != null && existing.mtime_ms !== fileMtimeMs) {
          this.store.updateFileMtime(existing.id, fileMtimeMs);
        }
        continue;
      }

      const entries = parseEnvFile(content);

      let fileId: number;
      if (existing) {
        fileId = existing.id;
        this.store.deleteEnvVarsByFile(fileId);
        this.store.updateFileHash(fileId, hash, content.length, fileMtimeMs);
      } else {
        fileId = this.store.insertFile(relPath, 'env', hash, content.length, null, fileMtimeMs);
        this.store.updateFileStatus(fileId, 'ok', 'config');
      }

      for (const entry of entries) {
        this.store.insertEnvVar(fileId, {
          key: entry.key,
          valueType: entry.valueType,
          valueFormat: entry.valueFormat,
          comment: entry.comment,
          quoted: entry.quoted,
          line: entry.line,
        });
      }

      logger.debug({ file: relPath, keys: entries.length }, '.env file indexed');
    }
  }
}
